import { useEffect, useState } from "react";
import { lei } from "@/shared/format";

type Suggested = {
  rule_id: string;
  menu_item_id: string;
  product_id: string;
  name: string;
  producer: string | null;
  story: string | null;
  price_minor: number;
  copy: string | null;
};

const T = {
  ro: { goesWith: "Merge bine cu", add: "Adaugă și asta", skip: "Nu, mulțumesc" },
  ru: { goesWith: "Хорошо сочетается с", add: "Добавить", skip: "Нет, спасибо" },
  en: { goesWith: "This goes well with", add: "Add that too", skip: "No, thank you" },
} as const;

/**
 * A pairing suggestion, in the voice of a host — not an upsell widget.
 *
 * The brief's rules, all enforced here:
 *   - at most two, never a grid (the API also caps at two)
 *   - the hosts' own copy, per language, never generated sales language
 *   - skipping costs one tap and is styled as an equal choice, not a dismissal
 *   - NO urgency: no timer, no "popular", no "only today", no red anything
 *
 * If nothing is suggested, this renders nothing at all rather than an empty box.
 */
export function Suggestion({
  eventSlug, productId, afterName, lang, onAdd, onClose,
}: {
  eventSlug: string;
  productId: string;
  afterName: string;
  lang: string;
  onAdd: (line: { menu_item_id: string; product_id: string; name: string; price_minor: number; upsell_rule_id?: string | null }) => void;
  onClose: () => void;
}) {
  const t = T[(lang in T ? lang : "ro") as keyof typeof T];
  const [items, setItems] = useState<Suggested[] | null>(null);

  useEffect(() => {
    let alive = true;
    fetch(`/api/suggestions/${encodeURIComponent(eventSlug)}/${encodeURIComponent(productId)}?lang=${lang}`)
      .then(async (r) => (r.ok ? ((await r.json()) as { suggestions: Suggested[] }) : { suggestions: [] }))
      .then((b) => {
        if (!alive) return;
        // Nothing to say? Then say nothing and get out of the guest's way.
        if (b.suggestions.length === 0) onClose();
        else setItems(b.suggestions);
      })
      .catch(() => onClose());
    return () => { alive = false; };
  }, [eventSlug, productId, lang, onClose]);

  if (!items || items.length === 0) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-dove-200 bg-paper p-5 shadow-[0_-2px_40px_-10px_rgba(83,54,36,0.15)]">
      <div className="mx-auto max-w-lg">
        <p className="text-ink-muted">
          {t.goesWith} <span className="text-ink">{afterName}</span>
        </p>

        <ul className="m-0 mt-3 list-none p-0">
          {items.map((s) => (
            <li key={s.rule_id} className="border-t border-dove-100 pt-3 first:border-t-0 first:pt-0">
              <div className="flex items-baseline justify-between gap-4">
                <span style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>{s.name}</span>
                <span className="tabular shrink-0">{lei(s.price_minor)}</span>
              </div>
              {s.producer && (
                <p className="text-olive-600" style={{ fontSize: "var(--text-kicker)" }}>{s.producer}</p>
              )}
              {/* The hosts' own words, when they wrote some. */}
              {s.copy && <p className="mt-1 italic text-ink-muted">{s.copy}</p>}

              <button
                onClick={() => {
                  onAdd({
                    menu_item_id: s.menu_item_id,
                    product_id: s.product_id,
                    name: s.name,
                    price_minor: s.price_minor,
                    upsell_rule_id: s.rule_id,
                  });
                  onClose();
                }}
                className="tap mt-3 w-full border border-olive-500 px-5 text-olive-600 hover:bg-olive-100"
                style={{ borderRadius: 0, fontFamily: "var(--font-display)" }}
              >
                {t.add}
              </button>
            </li>
          ))}
        </ul>

        <button
          onClick={onClose}
          className="tap mt-2 w-full px-5 text-ink-faint"
          style={{ borderRadius: 0 }}
        >
          {t.skip}
        </button>
      </div>
    </div>
  );
}
